import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Drawer,
  Grid,
  IconButton,
  Link,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  keyframes,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import logoImage from "../assets/LU-2026--Main-Revised.png";
import BiometricIllustration from "./landing/BiometricIllustration";
import ParticleCanvas from "./landing/ParticleCanvas";
import { LU_RED } from "../theme/luTheme";

function useCountUp(target, duration = 2000, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return undefined;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const p = Math.min((ts - startTime) / duration, 1);
      setVal(Math.floor(p * target));
      if (p < 1) requestAnimationFrame(step);
    };
    const id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [target, duration, start]);
  return val;
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;
const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

const sectionBadge = (color, border) => ({
  display: "inline-block",
  bgcolor: `${color}18`,
  border: `1px solid ${border}`,
  borderRadius: 50,
  px: 2,
  py: 0.625,
  fontSize: 12,
  color,
  fontWeight: 600,
  letterSpacing: 0.5,
  mb: 2.5,
});

const glassCardSx = {
  borderRadius: 3,
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: "rgba(99,179,237,0.25)",
    transform: "translateY(-4px)",
    boxShadow: "0 16px 40px rgba(0,0,0,0.08)",
  },
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const statsRef = useRef(null);

  const auth = useCountUp(997, 1800, statsVisible);
  const fraud = useCountUp(2400, 1800, statsVisible);
  const users = useCountUp(18, 1800, statsVisible);
  const uptime = useCountUp(9999, 1800, statsVisible);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setStatsVisible(true);
    }, { threshold: 0.3 });
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    setMobileNavOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const features = [
    { icon: "👁", title: "Face Verification", desc: "3D liveness detection with anti-spoofing AI. 99.98% accuracy.", color: "#63b3ed", glow: "rgba(99,179,237,0.3)" },
    { icon: "🎙", title: "Voice Authentication", desc: "Voice-print analysis with ambient noise cancellation.", color: "#9f7aea", glow: "rgba(159,122,234,0.3)" },
    { icon: "🔐", title: "Biometric Security", desc: "Military-grade encrypted biometric templates. Zero stored passwords.", color: "#68d391", glow: "rgba(104,211,145,0.3)" },
    { icon: "🧠", title: "AI Fraud Detection", desc: "Real-time behavioral analytics & anomaly detection.", color: "#fc8181", glow: "rgba(252,129,129,0.3)" },
    { icon: "📱", title: "Device Trust Engine", desc: "Device fingerprinting, certificate pinning & geo-trust scoring.", color: "#f6ad55", glow: "rgba(246,173,85,0.3)" },
    { icon: "⚡", title: "Multi-Factor Auth", desc: "Adaptive MFA that escalates only when risk is detected.", color: "#76e4f7", glow: "rgba(118,228,247,0.3)" },
  ];

  const industries = [
    { id: "finance", icon: "🏦", name: "Banking & Finance", color: "#63b3ed", bg: "rgba(99,179,237,0.12)", steps: ["User opens banking app", "Face scan initiated", "AI liveness check", "Transaction approved"], metrics: ["99.7% auth success", "0.003% fraud rate", "<1.2s verify time"] },
    { id: "healthcare", icon: "🏥", name: "Healthcare", color: "#68d391", bg: "rgba(104,211,145,0.12)", steps: ["Patient portal login", "Biometric verification", "HIPAA audit log", "Access granted"], metrics: ["HIPAA compliant", "PHI data protection", "Zero breach record"] },
    { id: "insurance", icon: "🛡️", name: "Insurance", color: "#f6ad55", bg: "rgba(246,173,85,0.12)", steps: ["Member authentication", "Policy access", "Claims submission", "Voice confirm"], metrics: ["40% faster claims", "99.2% uptime", "Fraud -67%"] },
    { id: "government", icon: "🏛️", name: "Government", color: "#9f7aea", bg: "rgba(159,122,234,0.12)", steps: ["Citizen ID scan", "Multi-biometric check", "Clearance verified", "Service unlocked"], metrics: ["FedRAMP ready", "FIPS 140-3", "ATO certified"] },
    { id: "corporate", icon: "🏢", name: "Enterprise SSO", color: "#fc8181", bg: "rgba(252,129,129,0.12)", steps: ["Employee badge scan", "AD/LDAP sync", "Zero-trust check", "Dashboard access"], metrics: ["SOC 2 Type II", "50k+ employees", "<800ms SSO"] },
  ];

  const sdkTabs = [
    { label: "NPM", code: `# Install the SDK\nnpm install @legitimatelyu/sdk\n\n# Initialize in your app\nimport { LegitimatelyU } from '@legitimatelyu/sdk';\n\nconst lu = new LegitimatelyU({\n  clientId: 'YOUR_CLIENT_ID',\n  modes: ['face', 'voice', 'biometric'],\n  trustLevel: 'enterprise',\n});\n\nconst result = await lu.authenticate({ userId });` },
    { label: "REACT", code: `import { useLegitimatelyU } from '@legitimatelyu/react';\n\nexport const LoginButton = () => {\n  const { authenticate, status } = useLegitimatelyU({\n    clientId: 'YOUR_CLIENT_ID',\n  });\n\n  return (\n    <button onClick={authenticate}>\n      {status === 'verifying' ? 'Scanning...' : 'Login with LU'}\n    </button>\n  );\n};` },
    { label: "API", code: `POST https://api.legitimatelyu.com/v1/authenticate\n\nAuthorization: Bearer YOUR_API_KEY\n\n{\n  "user_id": "usr_abc123",\n  "modes": ["face", "voice"],\n  "device_trust": true\n}\n\n// Response\n{\n  "verified": true,\n  "trust_score": 98.4,\n  "session_token": "eyJhbGci..."\n}` },
  ];

  const activityFeed = [
    { user: "sarah.k@acmebank.com", action: "Face verification", status: "✅", time: "2s ago", score: 99 },
    { user: "j.doe@healthcorp.io", action: "Voice auth", status: "✅", time: "5s ago", score: 97 },
    { user: "unknown@suspicious.ru", action: "Login attempt", status: "🚨", time: "8s ago", score: 12 },
    { user: "admin@govportal.gov", action: "Biometric scan", status: "✅", time: "12s ago", score: 98 },
    { user: "cfo@enterprise.com", action: "MFA escalation", status: "⚠️", time: "18s ago", score: 74 },
  ];

  const navItems = [
              { label: "Features", id: "features" },
              { label: "Portals", id: "industries" },
              { label: "SDK", id: "sdk" },
    { label: "Security", id: "security" },
  ];

  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary", overflowX: "hidden", pb: "env(safe-area-inset-bottom)" }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: "rgba(255,255,255,0.98)", borderBottom: 1, borderColor: "divider" }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, md: 70 }, justifyContent: "space-between", gap: 1.5, py: { xs: 0.5, md: 0 } }}>
            <Box component="img" src={logoImage} alt="Legitimately U" sx={{ height: { xs: 26, sm: 32 }, objectFit: "contain", flexShrink: 0 }} />
            <Stack direction="row" spacing={4} sx={{ display: { xs: "none", md: "flex" } }}>
              {navItems.map((it) => (
                <Link key={it.id} component="button" underline="none" onClick={() => scrollToSection(it.id)} sx={{ color: "text.secondary", fontSize: 14, fontWeight: 500, "&:hover": { color: "#63b3ed" } }}>
                  {it.label}
                </Link>
              ))}
            </Stack>
            <Stack direction="row" spacing={{ xs: 0.75, sm: 1.5 }} sx={{ alignItems: "center" }}>
              <IconButton aria-label="Open menu" onClick={() => setMobileNavOpen(true)} sx={{ display: { xs: "inline-flex", md: "none" } }}>
                <MenuIcon />
              </IconButton>
              <Link component="button" underline="none" sx={{ color: "text.secondary", fontSize: 14, fontWeight: 500, display: { xs: "none", sm: "inline" } }}>
                Sign In
              </Link>
              <Button
                variant="contained"
                size="small"
                sx={{
                  background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
                  boxShadow: "0 0 30px rgba(99,179,237,0.3)",
                  borderRadius: 50,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 0.75, sm: 1 },
                  fontSize: { xs: 12, sm: 14 },
                  whiteSpace: "nowrap",
                }}
              >
                Start Free
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        slotProps={{ paper: { sx: { width: 280, pt: "env(safe-area-inset-top)", pb: "env(safe-area-inset-bottom)" } } }}
      >
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Typography fontWeight={700}>Menu</Typography>
          <IconButton aria-label="Close menu" onClick={() => setMobileNavOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Stack spacing={0.5} sx={{ p: 2 }}>
          {navItems.map((it) => (
            <Button key={it.id} onClick={() => scrollToSection(it.id)} sx={{ justifyContent: "flex-start", color: "text.primary", fontWeight: 600, py: 1.25 }}>
              {it.label}
            </Button>
          ))}
          <Button sx={{ justifyContent: "flex-start", color: "text.secondary", py: 1.25 }}>Sign In</Button>
        </Stack>
      </Drawer>

      {/* Hero */}
      <Box component="section" sx={{ position: "relative", minHeight: { xs: "auto", md: "92vh" }, display: "flex", alignItems: "center", py: { xs: 4, sm: 6, md: 10 }, overflow: "hidden" }}>
        <Container maxWidth="lg" sx={{ position: "relative", px: { xs: 2, sm: 3 } }}>
        <ParticleCanvas />
          <Box sx={{ position: "absolute", top: "-10%", right: { xs: "-20%", md: "5%" }, width: { xs: 280, md: 500 }, height: { xs: 280, md: 500 }, borderRadius: "50%", background: "radial-gradient(circle,rgba(99,179,237,0.12),transparent 70%)", pointerEvents: "none" }} />
          <Grid container spacing={{ xs: 4, md: 6 }} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 6 }} sx={{ animation: `${fadeUp} 0.8s ease forwards` }}>
              <Chip
                label="Now SOC 2 Type II Certified"
                icon={<Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#68d391", animation: `${blink} 1.5s infinite`, ml: 0.5 }} />}
                sx={{ mb: { xs: 2, md: 3.5 }, bgcolor: "rgba(99,179,237,0.1)", border: "1px solid rgba(99,179,237,0.25)", color: "#63b3ed", fontWeight: 600, fontSize: { xs: 11, sm: 13 }, height: "auto", "& .MuiChip-label": { px: 1, py: 0.5, whiteSpace: "normal" } }}
              />
              <Typography variant="h2" sx={{ fontWeight: 900, lineHeight: 1.1, mb: { xs: 2, md: 3 }, fontSize: { xs: "1.85rem", sm: "2.2rem", md: "3.2rem" } }}>
                <Box component="span" sx={{ color: LU_RED }}>Unified Identity.</Box>
                <br />
              Biometric Authentication.
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.3rem" }, color: "text.secondary", lineHeight: 1.6, mb: { xs: 3, md: 5 }, maxWidth: 560 }}>
            The enterprise standard for high-trust biometric access. Secure your organization with sub-second spatial verification across every digital touchpoint.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: { xs: 4, md: 6 } }}>
                <Button variant="contained" size="large" fullWidth={false} onClick={() => window.open("https://web.legitimatelyu.com", "_blank")} sx={{ width: { xs: "100%", sm: "auto" }, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", borderRadius: 50, px: { xs: 3, sm: 4 }, py: 1.25, boxShadow: "0 0 30px rgba(99,179,237,0.3)", fontSize: { xs: 14, sm: 16 } }}>
              🚀 Start Integration
                </Button>
                <Button variant="outlined" size="large" sx={{ width: { xs: "100%", sm: "auto" }, borderRadius: 50, px: { xs: 3, sm: 4 }, py: 1.25, borderColor: "rgba(99,179,237,0.4)", color: "text.secondary", fontSize: { xs: 14, sm: 16 } }}>
                  📄 View SDK Docs
                </Button>
              </Stack>
              <Grid container spacing={2}>
                {[["99.7%", "Auth Accuracy"], ["<1.2s", "Verify Speed"], ["18M+", "Users Protected"]].map(([n, l]) => (
                  <Grid key={l} size={{ xs: 4, sm: "auto" }}>
                    <Typography sx={{ fontSize: { xs: "1.25rem", sm: "1.8rem" }, fontWeight: 800, background: "linear-gradient(135deg,#63b3ed,#9f7aea)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{n}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 10, sm: 12 }, lineHeight: 1.3, display: "block" }}>{l}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex", justifyContent: "center", mt: { xs: 1, md: 0 } }}>
              <BiometricIllustration />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Trusted by */}
      <Box sx={{ py: { xs: 2.5, md: 3 }, borderTop: 1, borderBottom: 1, borderColor: "divider", bgcolor: "grey.50" }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={{ xs: 1.5, md: 4 }} flexWrap="wrap" useFlexGap sx={{ alignItems: "center", justifyContent: "center", gap: { xs: 1, md: 2 } }}>
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: { xs: 1, md: 2 }, color: "text.secondary", width: { xs: "100%", sm: "auto" }, textAlign: "center" }}>TRUSTED BY ENTERPRISES</Typography>
            {["JPMorgan Chase", "Mayo Clinic", "Aetna Insurance", "US Federal Portal", "Fortune 500"].map((l) => (
              <Typography key={l} variant="body2" sx={{ fontWeight: 700, color: "text.secondary", fontSize: { xs: 12, sm: 14 }, textAlign: "center" }}>{l}</Typography>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Features */}
      <Box component="section" id="features" sx={{ py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 8 } }}>
            <Box sx={sectionBadge("#63b3ed", "rgba(99,179,237,0.2)")}>CAPABILITIES</Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: "1.65rem", sm: "2rem", md: "3rem" } }}>Every Biometric Layer Covered</Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 500, mx: "auto", px: { xs: 1, sm: 0 }, fontSize: { xs: 14, sm: 16 } }}>
            A unified SDK handling every authentication vector — from face and voice to behavioral signals and device signals.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {features.map((f) => (
              <Grid key={f.title} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Card sx={{ ...glassCardSx, height: "100%", p: 1, position: "relative", overflow: "hidden" }}>
                  <CardContent>
                    <Box sx={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: `radial-gradient(circle,${f.glow},transparent 70%)`, pointerEvents: "none" }} />
                    <Box sx={{ width: 52, height: 52, borderRadius: 1.75, border: `1px solid ${f.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, mb: 2.5 }}>{f.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25 }}>{f.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>{f.desc}</Typography>
                    <Typography sx={{ mt: 2.5, color: f.color, fontSize: 13, fontWeight: 600 }}>Learn more →</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Industries */}
      <Box component="section" id="industries" sx={{ py: { xs: 6, md: 12 }, bgcolor: "grey.50", borderTop: 1, borderColor: "divider" }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 8 } }}>
            <Box sx={sectionBadge("#9f7aea", "rgba(159,122,234,0.2)")}>INDUSTRY USE CASES</Box>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "1.65rem", sm: "2rem", md: "3rem" } }}>Built for Every Sector</Typography>
          </Box>
          <Grid container spacing={3}>
            {industries.map((ind) => (
              <Grid key={ind.id} size={{ xs: 12, md: 6, lg: 4 }}>
                <Card sx={{ ...glassCardSx, height: "100%", bgcolor: ind.bg, cursor: "pointer" }} onClick={() => navigate(`/${ind.id}`)}>
                  <CardContent>
                    <Stack direction="row" spacing={1.75} sx={{ alignItems: "center", mb: 3 }}>
                      <Box sx={{ width: 48, height: 48, borderRadius: 1.5, bgcolor: `${ind.color}18`, border: `1px solid ${ind.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{ind.icon}</Box>
                      <Box>
                        <Typography fontWeight={700}>{ind.name}</Typography>
                        <Typography variant="caption" color="text.secondary">Integration Preview</Typography>
                      </Box>
                    </Stack>
                    <Stack spacing={1.25} sx={{ mb: 2.5 }}>
                      {ind.steps.map((s, j) => (
                        <Stack key={s} direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: ind.color, mt: 0.75, flexShrink: 0, boxShadow: `0 0 8px ${ind.color}` }} />
                          <Typography variant="body2" color="text.secondary">{s}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {ind.metrics.map((m) => (
                        <Chip key={m} label={m} size="small" sx={{ fontSize: 11, fontWeight: 600, color: ind.color, bgcolor: `${ind.color}15`, border: `1px solid ${ind.color}33` }} />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SDK */}
      <Box component="section" id="sdk" sx={{ py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 4, md: 8 }} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={sectionBadge("#68d391", "rgba(104,211,145,0.2)")}>DEVELOPER FIRST</Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 2.5, lineHeight: 1.2, fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" } }}>
                Integrate in Minutes,<br />
                <Box component="span" sx={{ color: "#68d391" }}>Not Months</Box>
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4, lineHeight: 1.75, fontSize: { xs: 14, sm: 16 } }}>
                Drop-in SDK for React, Vue, Angular, iOS, Android and plain JS. Full REST API with WebSocket streams for real-time auth events.
              </Typography>
              <Stack spacing={2} sx={{ mb: 4 }}>
                {[
                  { n: "1", t: "Install SDK", d: "One npm/yarn command" },
                  { n: "2", t: "Configure Client ID", d: "Paste your key" },
                  { n: "3", t: "Authenticate Users", d: "One function call" },
                ].map((s) => (
                  <Stack key={s.n} direction="row" spacing={2} sx={{ alignItems: "center" }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 1, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{s.n}</Box>
                    <Box>
                      <Typography fontWeight={600}>{s.t}</Typography>
                      <Typography variant="body2" color="text.secondary">{s.d}</Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
              <Button variant="contained" sx={{ background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", borderRadius: 50, px: 3 }}>📚 Read the Docs</Button>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ ...glassCardSx, overflow: "hidden" }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{ px: { xs: 1, sm: 2 }, borderBottom: 1, borderColor: "divider", minHeight: 44 }}
                >
                  {sdkTabs.map((t, i) => (
                    <Tab key={t.label} label={t.label} value={i} sx={{ fontWeight: 600, fontSize: { xs: 12, sm: 13 }, minHeight: 44, px: { xs: 1.5, sm: 2 } }} />
                  ))}
                </Tabs>
                <Box component="pre" sx={{ p: { xs: 2, sm: 3 }, m: 0, fontSize: { xs: 11, sm: 12.5 }, lineHeight: 1.7, color: "text.secondary", overflowX: "auto", fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word", WebkitOverflowScrolling: "touch" }}>
                  {sdkTabs[activeTab].code}
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Security / Analytics */}
      <Box component="section" id="security" ref={statsRef} sx={{ py: { xs: 6, md: 12 }, bgcolor: "grey.50", borderTop: 1, borderColor: "divider" }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 8 } }}>
            <Box sx={sectionBadge("#fc8181", "rgba(252,129,129,0.2)")}>LIVE DASHBOARD</Box>
            <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" } }}>Enterprise Analytics at a Glance</Typography>
          </Box>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {[
              { label: "Auth Success Rate", value: `${(auth / 10).toFixed(1)}%`, icon: "✅", color: "#68d391" },
              { label: "Fraud Blocked Today", value: fraud.toLocaleString(), icon: "🚨", color: "#fc8181" },
              { label: "Active Users (M)", value: `${users}M+`, icon: "👥", color: "#63b3ed" },
              { label: "Platform Uptime", value: `${(uptime / 100).toFixed(2)}%`, icon: "⚡", color: "#9f7aea" },
            ].map((s) => (
              <Grid key={s.label} size={{ xs: 6, md: 3 }}>
                <Card sx={glassCardSx}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
                    <Typography sx={{ fontSize: { xs: 22, sm: 28 }, mb: 0.75 }}>{s.icon}</Typography>
                    <Typography sx={{ fontSize: { xs: "1.35rem", sm: "2rem" }, fontWeight: 800, color: s.color, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>{s.value}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 10, sm: 12 }, lineHeight: 1.3, display: "block", mt: 0.5 }}>{s.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Card sx={{ ...glassCardSx, overflow: "hidden" }}>
            <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", px: { xs: 2, sm: 3.5 }, py: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography fontWeight={700} sx={{ fontSize: { xs: 14, sm: 16 } }}>🔴 Live Activity Feed</Typography>
              <Chip label="LIVE" size="small" sx={{ color: "#68d391", bgcolor: "rgba(104,211,145,0.1)", border: "1px solid rgba(104,211,145,0.2)", fontWeight: 600, fontSize: { xs: 10, sm: 12 } }} />
            </Stack>

            <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" }, p: 2 }}>
              {activityFeed.map((row) => (
                <Box key={row.user} sx={{ p: 1.5, borderRadius: 1.5, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                  <Stack direction="row" spacing={1.25} sx={{ alignItems: "center", mb: 1 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: row.score > 80 ? "rgba(99,179,237,0.15)" : row.score > 50 ? "rgba(246,173,85,0.15)" : "rgba(252,129,129,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>👤</Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13, wordBreak: "break-all" }}>{row.user}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.action}</Typography>
                    </Box>
                    <Typography sx={{ flexShrink: 0 }}>{row.status}</Typography>
                  </Stack>
                  <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="caption" color="text.secondary">{row.time}</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ color: row.score > 80 ? "#68d391" : row.score > 50 ? "#f6ad55" : "#fc8181" }}>
                      Trust {row.score}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>

            <Box sx={{ display: { xs: "none", md: "block" } }}>
            {activityFeed.map((row, i) => (
              <Stack key={row.user} direction="row" spacing={2} sx={{ alignItems: "center", px: 3.5, py: 2, borderBottom: i < activityFeed.length - 1 ? 1 : 0, borderColor: "divider", "&:hover": { bgcolor: "action.hover" } }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flex: 1, minWidth: 0 }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: 1.25, bgcolor: row.score > 80 ? "rgba(99,179,237,0.15)" : row.score > 50 ? "rgba(246,173,85,0.15)" : "rgba(252,129,129,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>👤</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap>{row.user}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.action}</Typography>
                  </Box>
                </Stack>
                <Typography>{row.status}</Typography>
                <Box sx={{ textAlign: "right", minWidth: 72 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ color: row.score > 80 ? "#68d391" : row.score > 50 ? "#f6ad55" : "#fc8181" }}>{row.score}</Typography>
                  <Typography variant="caption" color="text.secondary">Trust Score</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: "right" }}>{row.time}</Typography>
              </Stack>
            ))}
            </Box>
          </Card>
        </Container>
      </Box>

      {/* CTA */}
      <Box component="section" id="cta" sx={{ py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Card sx={{ borderRadius: 4, p: { xs: 3, sm: 4, md: 10 }, textAlign: "center", position: "relative", overflow: "hidden", background: "linear-gradient(135deg,rgba(59,130,246,0.15),rgba(139,92,246,0.15))", border: "1px solid rgba(99,179,237,0.2)" }}>
            <Typography variant="h3" sx={{ fontWeight: 900, mb: 2.5, fontSize: { xs: "1.65rem", sm: "2rem", md: "3rem" }, background: "linear-gradient(135deg,#63b3ed,#9f7aea,#68d391)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundSize: "200%", animation: `${gradientShift} 4s ease infinite` }}>
              Ready to Go Passwordless?
            </Typography>
            <Typography color="text.secondary" sx={{ mb: { xs: 3, md: 5 }, maxWidth: 480, mx: "auto", fontSize: { xs: 14, sm: 16 }, px: { xs: 0.5, sm: 0 } }}>
            Join 500+ enterprises using LegitimatelyU to protect millions of users with AI-powered biometric authentication.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ justifyContent: "center", alignItems: "stretch", maxWidth: 420, mx: "auto" }}>
              <Button variant="contained" size="large" onClick={() => window.open("https://web.legitimatelyu.com", "_blank")} sx={{ width: { xs: "100%", sm: "auto" }, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", borderRadius: 50, px: { xs: 3, sm: 5 }, py: 1.25 }}>
              🚀 Start Free Integration
              </Button>
              <Button variant="outlined" size="large" sx={{ width: { xs: "100%", sm: "auto" }, borderRadius: 50, px: { xs: 3, sm: 5 }, py: 1.25, borderColor: "rgba(99,179,237,0.4)" }}>
                📞 Talk to Sales
              </Button>
            </Stack>
          </Card>
        </Container>
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ borderTop: 1, borderColor: "divider", py: { xs: 4, md: 5 }, pb: { xs: "calc(16px + env(safe-area-inset-bottom))", md: 5 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} textAlign={{ xs: "center", md: "left" }} sx={{ justifyContent: "space-between", alignItems: { xs: "center", md: "center" } }}>
            <Stack direction="row" spacing={1.25} sx={{ alignItems: "center", justifyContent: "center" }}>
              <Box component="img" src={logoImage} alt="Legitimately U" sx={{ height: { xs: 24, sm: 28 } }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 12, sm: 14 } }}>© 2026 All rights reserved.</Typography>
            </Stack>
            <Stack direction="row" spacing={{ xs: 2, md: 3.5 }} flexWrap="wrap" useFlexGap sx={{ justifyContent: "center" }}>
              {["Privacy Policy", "Terms", "Security", "Status", "Contact"].map((l) => (
                <Link key={l} component="button" underline="none" sx={{ fontSize: { xs: 12, sm: 13 }, color: "text.secondary", "&:hover": { color: "#63b3ed" } }}>{l}</Link>
              ))}
            </Stack>
            <Chip label="✅ All Systems Operational" size="small" sx={{ color: "#68d391", bgcolor: "rgba(104,211,145,0.1)", border: "1px solid rgba(104,211,145,0.2)", fontWeight: 600, fontSize: { xs: 11, sm: 12 } }} />
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
